import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import {
  buildTaxInvoiceListQuery,
  normalizeTaxInvoiceList,
  type TaxInvoiceListResponse,
  type TaxInvoiceQueryParams,
} from "helpers/taxInvoicePagination";
import type { InvoiceStatus } from "types";
import type {
  AUMStats,
  BuyerAUMStats,
  GenerateInvoice,
  GeneratedInvoice,
  InvoiceStats,
  SOFRRate,
  TaxInvoice,
  TaxInvoiceData,
} from "./api-operations.d";

export interface MonthlyInvoiceStats {
  total_amount_financed: number;
  financed_per_buyer: {
    buyer: string;
    total_financed_amount: number;
    percentage: number;
  }[];
}

export interface DailyInvoiceStats {
  date: string;
  financed_per_buyer: {
    buyer: string;
    amount: number;
  }[];
}

export interface PlatformInvoiceStats {
  status: string;
  amount: number;
}

export interface AuthorizationLetter {
  id: string;
  contact_id: string;
  name: string;
  url: string;
  document_type: string;
  submitted_at: string;
}

export interface BuyerConfirmationLetter {
  invoice_id: string;
  email_id: string;
  to: string[];
  subject: string;
  body: string;
  sent_at: string;
}

export interface EmailStatus {
  id: string;
  email: string;
  entity_type: string;
  invoice_id: string;
  buyer_confirmation_status?: string | null;
  sla_status?: string | null;
  action_taken?: string | null;
  action_taken_at?: string | null;
  bc_action?: string | null;
  bc_action_at?: string | null;
  system_info?: {
    ip_address?: string | null;
    screen?: string | null;
    language?: string | null;
    platform?: string | null;
    timezone?: string | null;
    user_agent?: string | null;
    [key: string]: unknown;
  } | string | null;
  acted_by?: string | null;
  created_at: string;
  expires_at: string;
}

export interface SupplierApprovalDetails {
  invoice_id: string;
  invoice_number: string;
  status: string;
  sent_date: string | null;
  approved_date: string | null;
  contact: {
    id: string;
    name: string;
    email: string;
    phone: string;
  } | null;
  supplier_approval_details: string;
}

export const operations = createApi({
  reducerPath: "operations",
  tagTypes: [
    "INVOICE_STATUS",
    "ROLLS",
    "TAX_INVOICES",
    "SF_TAX_INVOICES",
    "TAX_INVOICE",
    "SOFR_RATES",
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}/operations`,
  }),
  endpoints: (builder) => ({
    getInvoiceStatuses: builder.query<
      {
        [statusName: string]: InvoiceStatus[];
      },
      null
    >({
      query: () => ({ url: "/invoice-statuses", method: "get" }),
      transformResponse: (response) => response?.data,
      providesTags: ["INVOICE_STATUS"],
    }),
    getRolls: builder.query<
      {
        [statusName: string]: InvoiceStatus[];
      },
      null
    >({
      query: () => ({ url: "/invoice-rolls", method: "get" }),
      transformResponse: (response) => response?.data,
      providesTags: ["ROLLS"],
    }),
    getInvoiceStats: builder.query<InvoiceStats, string>({
      query: (invoice_id) => ({ url: `/invoice/${invoice_id}`, method: "get" }),
      transformResponse: (response) => response?.data,
    }),
    getMonthlyInvoiceStats: builder.query<MonthlyInvoiceStats, null>({
      query: () => ({
        url: `/invoices/monthly-financed`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getDailyInvoiceStats: builder.query<DailyInvoiceStats[], null>({
      query: () => ({
        url: `/invoices/monthly-financed-by-day`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getAUM: builder.query<AUMStats[], null>({
      query: () => ({
        url: `/invoices/assets-under-management`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getBuyerAUM: builder.query<BuyerAUMStats[], null>({
      query: () => ({
        url: `/invoices/assets-under-management-by-buyer`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getPlatformInvoiceStats: builder.query<PlatformInvoiceStats[], null>({
      query: () => ({
        url: `/invoices/available-on-platform`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),

    getTaxInvoices: builder.query<TaxInvoice[], string>({
      query: (sf_invoice_name) => ({
        url: `/invoices/${sf_invoice_name}/tax-invoices`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SF_TAX_INVOICES"],
    }),
    getTaxInvoice: builder.query<
      TaxInvoiceData,
      { sf_invoice_name: string; sf_tax_invoice_id: string }
    >({
      query: ({ sf_invoice_name, sf_tax_invoice_id }) => ({
        url: `/invoices/${sf_invoice_name}/tax-invoices/${sf_tax_invoice_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    generateTaxInvoice: builder.mutation<
      any,
      GenerateInvoice & { sf_invoice_name: string }
    >({
      query: (payload) => {
        const { sf_invoice_name, ...data } = payload;
        return {
          url: `/invoices/${sf_invoice_name}/tax-invoices`,
          method: "post",
          data,
        };
      },
      invalidatesTags: ["TAX_INVOICES", "SF_TAX_INVOICES"],
    }),
    getGeneratedTaxInvoices: builder.query<TaxInvoiceListResponse, TaxInvoiceQueryParams>({
      query: buildTaxInvoiceListQuery,
      transformResponse: (
        response: Parameters<typeof normalizeTaxInvoiceList>[0],
        _meta,
        params,
      ) => normalizeTaxInvoiceList(response, params),
      providesTags: ["TAX_INVOICES"],
    }),
    getGeneratedTaxInvoice: builder.query<GeneratedInvoice, string>({
      query: (internal_name) => ({
        url: `/tax-invoices/${internal_name}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["TAX_INVOICE"],
    }),
    getSofrRates: builder.query<SOFRRate[], { from?: string; to?: string }>({
      query: ({ from, to }) => ({
        url: `/sofr-rates${from && to ? `?from=${from}&to=${to}` : ""}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SOFR_RATES"],
    }),
    addSofrRate: builder.mutation<
      SOFRRate,
      {
        date: string; // YYYY-MM-DD
        rate: number;
      }
    >({
      query: (data) => ({
        url: `/sofr-rates`,
        method: "post",
        data,
      }),
      invalidatesTags: ["SOFR_RATES"],
    }),
    getEmailStatus: builder.query<
      EmailStatus,
      { invoice_id: string; entity_type: string }
    >({
      query: ({ invoice_id, entity_type }) => ({
        url: `/email-status/${invoice_id}?entity_type=${entity_type}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getBuyerConfirmationLetter: builder.query<
      BuyerConfirmationLetter,
      string
    >({
      query: (invoiceId) => ({
        url: `/invoices/${invoiceId}/buyer-confirmation-letter`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getSupplierApproval: builder.query<SupplierApprovalDetails, string>({
      query: (invoiceId) => ({
        url: `/invoices/${encodeURIComponent(invoiceId)}/supplier-approval`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getAuthorizationLetters: builder.query<AuthorizationLetter[], string>({
      query: (mapName) => ({
        url: `/map/${encodeURIComponent(mapName)}/authorization-letters`,
        method: "get",
      }),
      transformResponse: (response) => response?.data ?? [],
    }),
    getSupportingDocuments: builder.query<any, string>({
      query: (account_relationship_id) => ({
        url: `/supporting-documents`,
        method: "get",
        params: {
          account_relationship_id,
        },
      }),
      transformResponse: (response) => response?.data,
    }),
    SupportingDocuments: builder.mutation<
      any,
      {
        supporting_document: string;
        account_relationship_id?: string;
      }
    >({
      query: ({ supporting_document, account_relationship_id }) => ({
        url: `/supporting-documents`,
        method: "post",
        data: {
          supporting_document,
        },
        params: {
          account_relationship_id,
        },
      }),
      transformResponse: (response) => response?.data,
    }),
    updateSupportingDocuments: builder.mutation<
      any,
      {
        supporting_document: string;
        account_relationship_id?: string;
      }
    >({
      query: ({ supporting_document, account_relationship_id }) => ({
        url: `/supporting-documents`,
        method: "patch",
        data: {
          supporting_document,
        },
        params: {
          account_relationship_id,
        },
      }),
      transformResponse: (response) => response?.data,
    }),
  }),
});

export const {
  useGetInvoiceStatusesQuery,
  useGetRollsQuery,
  useGetInvoiceStatsQuery,
  useGetMonthlyInvoiceStatsQuery,
  useGetDailyInvoiceStatsQuery,
  useGetPlatformInvoiceStatsQuery,
  useGetTaxInvoicesQuery,
  useGetTaxInvoiceQuery,
  useGenerateTaxInvoiceMutation,
  useGetGeneratedTaxInvoicesQuery,
  useLazyGetGeneratedTaxInvoicesQuery,
  useGetGeneratedTaxInvoiceQuery,
  useGetSofrRatesQuery,
  useAddSofrRateMutation,
  useGetAUMQuery,
  useGetBuyerAUMQuery,
  useGetEmailStatusQuery,
  useLazyGetBuyerConfirmationLetterQuery,
  useGetSupplierApprovalQuery,
  useGetAuthorizationLettersQuery,
  useGetSupportingDocumentsQuery,
  useSupportingDocumentsMutation,
  useUpdateSupportingDocumentsMutation,
} = operations;

// Keep endpoint definitions and Redux middleware on the same API instance in Vite.
if (import.meta.hot) {
  import.meta.hot.accept(() => window.location.reload());
}
